import type { Metric, ReportHandler } from "web-vitals";

export type WebVitalPayload = {
  name: Metric["name"];
  value: number;
  id: string;
  delta: number;
  page: string;
  timestamp: number;
};

/** True when the default collector should subscribe to web-vitals. */
export function shouldCollectWebVitals(options?: {
  nodeEnv?: string;
  endpoint?: string | undefined;
  hasCustomHandler?: boolean;
}): boolean {
  if (options?.hasCustomHandler) return true;
  const nodeEnv = options?.nodeEnv ?? process.env.NODE_ENV;
  if (nodeEnv === "development") return true;
  const endpoint =
    options?.endpoint ?? process.env.REACT_APP_WEB_VITALS_ENDPOINT;
  return Boolean(endpoint && endpoint.trim());
}

export function buildWebVitalPayload(
  metric: Pick<Metric, "name" | "value" | "id" | "delta">,
  page = typeof window !== "undefined" ? window.location.pathname : "/"
): WebVitalPayload {
  return {
    name: metric.name,
    value: metric.value,
    id: metric.id,
    delta: metric.delta,
    page,
    timestamp: Date.now(),
  };
}

/**
 * Best-effort POST to the configured analytics/collector endpoint.
 * Uses sendBeacon when available so metrics survive page unload.
 */
export function postWebVital(
  endpoint: string,
  payload: WebVitalPayload,
  transport: {
    sendBeacon?: (url: string, data?: BodyInit | null) => boolean;
    fetch?: typeof fetch;
  } = typeof navigator !== "undefined"
    ? { sendBeacon: navigator.sendBeacon?.bind(navigator), fetch }
    : { fetch: typeof fetch !== "undefined" ? fetch : undefined }
): void {
  const body = JSON.stringify(payload);
  try {
    if (transport.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const queued = transport.sendBeacon(endpoint, blob);
      if (queued) return;
    }
    void transport.fetch?.(endpoint, {
      method: "POST",
      body,
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      mode: "cors",
      credentials: "omit",
    });
  } catch {
    // Never break the app over observability
  }
}

export function createDefaultWebVitalsHandler(options?: {
  nodeEnv?: string;
  endpoint?: string | undefined;
  log?: (...args: unknown[]) => void;
  post?: typeof postWebVital;
}): ReportHandler {
  const nodeEnv = options?.nodeEnv ?? process.env.NODE_ENV;
  const endpoint =
    options?.endpoint ?? process.env.REACT_APP_WEB_VITALS_ENDPOINT;
  const log = options?.log ?? console.debug.bind(console);
  const post = options?.post ?? postWebVital;

  return (metric) => {
    const payload = buildWebVitalPayload(metric);

    if (nodeEnv === "development") {
      log("[web-vitals]", payload.name, Math.round(payload.value), payload);
    }

    if (nodeEnv === "production" && endpoint && endpoint.trim()) {
      post(endpoint.trim(), payload);
    }
  };
}

const reportWebVitals = (onPerfEntry?: ReportHandler) => {
  const hasCustomHandler =
    typeof onPerfEntry === "function" && onPerfEntry instanceof Function;

  if (
    !shouldCollectWebVitals({
      hasCustomHandler,
    })
  ) {
    return;
  }

  const handler = hasCustomHandler
    ? (onPerfEntry as ReportHandler)
    : createDefaultWebVitalsHandler();

  import("web-vitals").then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    getCLS(handler);
    getFID(handler);
    getFCP(handler);
    getLCP(handler);
    getTTFB(handler);
  });
};

export default reportWebVitals;
